import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  setDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db, getDocuments } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  Truck, 
  RefreshCw, 
  Package, 
  ArrowLeft,
  Calculator,
  DollarSign,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Clock,
  Users,
  User,
  FileText
} from 'lucide-react';
import { SettlementData } from '../types/inventory';
import { Warehouse, Vehicle, Route } from '../types';
import { UnifiedInventoryTransactionService } from '../services/unifiedInventoryTransactions';
import { getVisitStatsForRouteDate, RouteVisitStats } from '../services/outletVisits';

interface SettlementPageProps {
  onBack: () => void;
}

interface ProductSettlement {
  productId: string;
  productName: string;
  unit: string;
  category: string;
  openingStock: number;
  sales: number;
  replace: number;
  currentStock: number;
  physicalStock: number;
  variance: number;
}

interface CashCredit {
  totalCashSale: number;
  amountReceived: number;
  creditSale: number;
  invoiceList: string[];
}

const SettlementPage: React.FC<SettlementPageProps> = ({ onBack }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  
  // Selection states
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(''); // Auto-detected earliest unsettled date
  const [settlementInfo, setSettlementInfo] = useState<{date: string; routeCount: number; message: string} | null>(null);
  
  // Data states
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [productSettlements, setProductSettlements] = useState<ProductSettlement[]>([]);
  
  // UI states
  const [activeVehicleTab, setActiveVehicleTab] = useState<string>('');
  const [activeRouteTab, setActiveRouteTab] = useState<string>('');
  
  // Vehicle status tracking for settlement restrictions
  const [vehicleStatuses, setVehicleStatuses] = useState<{[vehicleId: string]: any}>({});
  
  // Store cleanup functions for real-time listeners
  const [statusUnsubscribers, setStatusUnsubscribers] = useState<(() => void)[]>([]);
  
  // Vehicle inventory data
  const [vehicleInventory, setVehicleInventory] = useState<{[vehicleId: string]: {[productId: string]: number}}>({});
  
  // Sales data for the day - now from salesHeaders/salesDetails
  const [vehicleSalesData, setVehicleSalesData] = useState<{[vehicleId: string]: {
    salesHeaders: any[];
    salesDetails: any[];
    productSummary: {[productId: string]: {sales: number; replace: number}};
  }}>({});
  const [routeSalesData, setRouteSalesData] = useState<{[routeId: string]: {
    salesHeaders: any[];
    salesDetails: any[];
    productSummary: {[productId: string]: {sales: number; replace: number}};
  }}>({});
  // Map vehicleId to routeId for selected date
  const [vehicleToRoute, setVehicleToRoute] = useState<{[vehicleId: string]: string}>({});
  // Map routeId to assignment info (salesman/vehicle)
  const [routeAssignments, setRouteAssignments] = useState<{[routeId: string]: { vehicleId?: string; vehicleName?: string; salesmanId?: string; salesmanName?: string }}>({});
  // Route status tracking
  const [routeStatuses, setRouteStatuses] = useState<{[routeId: string]: any}>({});
  // Detect earliest unsettled date for the selected warehouse
  const detectEarliestUnsettledDate = async (warehouseCustomId: string) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Query for non-settled statuses to avoid != inequality filter
      const unsettledQuery = query(
        collection(db, 'route_daily_status'),
        where('warehouseId', '==', warehouseCustomId),
        where('status', 'in', ['planning', 'issuing', 'on_field', 'settling'])
      );
      
      const unsettledSnapshot = await getDocs(unsettledQuery);
      const unsettledRoutes = unsettledSnapshot.docs.map(doc => doc.data());
      
      // Filter for dates before today and find earliest
      const pastUnsettledRoutes = unsettledRoutes.filter(route => route.date < today);
      
      if (pastUnsettledRoutes.length === 0) {
        // No unsettled routes, default to today for current operations
        setSelectedDate(today);
        setSettlementInfo({
          date: today,
          routeCount: 0,
          message: 'All previous routes settled. Showing today\'s operations.'
        });
        return;
      }
      
      // Find earliest unsettled date
      const earliestDate = pastUnsettledRoutes.map(route => route.date).sort()[0];
      const routesForEarliestDate = pastUnsettledRoutes.filter(route => route.date === earliestDate);
      
      setSelectedDate(earliestDate);
      setSettlementInfo({
        date: earliestDate,
        routeCount: routesForEarliestDate.length,
        message: `Settlement Required: ${earliestDate} (${routesForEarliestDate.length} route${routesForEarliestDate.length > 1 ? 's' : ''} pending)`
      });
      
      console.log('📅 Auto-detected settlement date:', earliestDate, 'Routes:', routesForEarliestDate.length);
    } catch (error) {
      console.error('Error detecting earliest unsettled date:', error);
      // Fallback to today
      const today = new Date().toISOString().split('T')[0];
      setSelectedDate(today);
      setSettlementInfo({
        date: today,
        routeCount: 0,
        message: 'Error detecting unsettled routes. Showing today\'s operations.'
      });
    }
  };

  // Outlet visit stats for selected route/date
  const [visitStats, setVisitStats] = useState<RouteVisitStats | null>(null);
  
  // Cash and Credit data
  const [cashCredit, setCashCredit] = useState<CashCredit>({
    totalCashSale: 0,
    amountReceived: 0,
    creditSale: 0,
    invoiceList: []
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      loadWarehouseData();
      // Auto-detect earliest unsettled date
      const warehouse = warehouses.find(w => w.id === selectedWarehouse);
      if (warehouse) {
        const warehouseCustomId = warehouse.warehouseId || selectedWarehouse;
        detectEarliestUnsettledDate(warehouseCustomId);
      }
    }
  }, [selectedWarehouse, warehouses]);

  useEffect(() => {
    if (vehicles.length > 0 && selectedWarehouse && selectedDate) {
      loadVehicleData();
      const setupListeners = async () => {
        const cleanup = await loadVehicleStatuses();
        if (cleanup) {
          setStatusUnsubscribers(prev => [...prev, cleanup]);
        }
      };
      setupListeners();
    }
  }, [vehicles, selectedWarehouse, selectedDate]);

  // Route status listener when routes are available
  useEffect(() => {
    if (!selectedWarehouse || routes.length === 0) return;
    const subscribe = async () => {
      const wh = warehouses.find(w => w.id === selectedWarehouse);
      const warehouseCustomId = wh?.warehouseId || selectedWarehouse;
      const statusQueryRef = query(
        collection(db, 'route_daily_status'),
        where('warehouseId', '==', warehouseCustomId),
        where('date', '==', selectedDate)
      );
      const unsubscribe = onSnapshot(statusQueryRef, (snapshot) => {
        const states: {[rid: string]: any} = {};
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data() as any;
          const rid = data.routeId;
          if (rid) states[rid] = data;
        });
        setRouteStatuses(states);
      });
      setStatusUnsubscribers(prev => [...prev, unsubscribe]);
    };
    subscribe();
  }, [routes, selectedWarehouse, selectedDate]);

  // Cleanup real-time listeners on component unmount
  useEffect(() => {
    return () => {
      statusUnsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [statusUnsubscribers]);

  useEffect(() => {
    if (vehicles.length > 0 && !activeVehicleTab) {
      setActiveVehicleTab(vehicles[0].vehicleId);
    }
  }, [vehicles]);

  useEffect(() => {
    if (activeRouteTab && selectedDate) {
      loadVehicleSettlementData();
      // Load outlet visit stats (scoped to assigned salesman if available)
      (async () => {
        try {
          const assign = routeAssignments[activeRouteTab];
          const stats = await getVisitStatsForRouteDate(activeRouteTab, selectedDate, assign?.salesmanId);
          setVisitStats(stats);
        } catch (e) {
          console.error('Error loading route visit stats:', e);
          setVisitStats(null);
        }
      })();
    }
  }, [activeRouteTab, routeSalesData, selectedDate]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load warehouses using the same pattern as LoadoutPage
      const [warehousesData, routesData, beatsData] = await Promise.all([
        getDocuments('warehouses'),
        getDocuments('routes'), 
        getDocuments('beats')
      ]);
      
      // Get current user's active route from beats table
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '-');
      const userActiveBeat = beatsData.find((beat: any) => 
        beat.salesmanId === currentUser?.id && 
        beat.isActive && 
        beat.forDate === today
      );
      
      // Filter warehouses based on user's route assignment
      let availableWarehouses = warehousesData.filter((warehouse: any) => warehouse.isActive !== false);
      
      if (userActiveBeat && userActiveBeat.routeId) {
        const userRoute = routesData.find((route: any) => route.id === userActiveBeat.routeId);
        if (userRoute && userRoute.warehouseId) {
          const userWarehouse = warehousesData.find((w: any) => w.id === userRoute.warehouseId);
          if (userWarehouse) {
            availableWarehouses = [
              userWarehouse,
              ...warehousesData.filter((w: any) => 
                w.id !== userRoute.warehouseId && w.isActive !== false
              )
            ];
          }
        }
      }
      
      setWarehouses(availableWarehouses);
      
      // Auto-select user's assigned warehouse if available
      if (availableWarehouses.length > 0 && !selectedWarehouse) {
        const userWarehouseId = userActiveBeat?.routeId ? 
          routesData.find((route: any) => route.id === userActiveBeat.routeId)?.warehouseId : null;
        
        const warehouseToSelect = userWarehouseId && availableWarehouses.find((w: any) => w.id === userWarehouseId) ?
          userWarehouseId : availableWarehouses[0].id;
          
        setSelectedWarehouse(warehouseToSelect);
      }
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouseData = async () => {
    if (!selectedWarehouse) return;
    
    try {
      setLoading(true);
      
      // 🔧 FIX: First get the selected warehouse document to get its warehouseId field
      const selectedWarehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
      if (!selectedWarehouseDoc || !selectedWarehouseDoc.warehouseId) {
        console.error('Selected warehouse not found or missing warehouseId:', selectedWarehouse);
        setVehicles([]);
        setLoading(false);
        return;
      }
      
      console.log('🔍 Querying vehicles for warehouseId:', selectedWarehouseDoc.warehouseId);
      
      // Load routes for this warehouse
      const routesSnapshot = await getDocs(
        query(
          collection(db, 'routes'),
          where('warehouseId', '==', selectedWarehouseDoc.warehouseId)
        )
      );
      const routesData = routesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Route[];
      setRoutes(routesData);
      if (!activeRouteTab && routesData.length > 0) {
        const firstWithCustom = routesData.find(r => (r as any).routeId);
        if (firstWithCustom?.routeId) setActiveRouteTab(firstWithCustom.routeId);
      }

      // Build vehicle->route map and get salesman assignments from route_daily_status for the selected date
      const routeDailyStatusSnapshot = await getDocs(
        query(
          collection(db, 'route_daily_status'),
          where('date', '==', selectedDate),
          where('warehouseId', '==', selectedWarehouseDoc.warehouseId)
        )
      );
      
      const mapping: {[vid: string]: string} = {};
      const assignments: {[rid: string]: { vehicleId?: string; vehicleName?: string; salesmanId?: string; salesmanName?: string }} = {};
      const assignedVehicleIds = new Set<string>();
      
      // Get all users to resolve salesman names
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userMap = new Map();
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        userMap.set(doc.id, userData.name);
      });

      routeDailyStatusSnapshot.docs.forEach(d => {
        const routeStatus: any = d.data();
        console.log('🔍 DEBUG: Route daily status data:', {
          docId: d.id,
          routeId: routeStatus.routeId,
          vehicleId: routeStatus.vehicleId, 
          vehicleNumber: routeStatus.vehicleNumber,
          salesmanId: routeStatus.salesmanId,
          status: routeStatus.status,
          date: routeStatus.date
        });
        
        if (routeStatus.vehicleId && routeStatus.routeId) {
          mapping[routeStatus.vehicleId] = routeStatus.routeId;
          // Ensure assignment belongs to this warehouse
          const r = routesData.find(r => (r as any).routeId === routeStatus.routeId);
          if (r && r.warehouseId === selectedWarehouseDoc.warehouseId) {
            const salesmanName = routeStatus.salesmanId ? userMap.get(routeStatus.salesmanId) || 'Unknown User' : null;
            assignments[routeStatus.routeId] = {
              vehicleId: routeStatus.vehicleId,
              vehicleName: routeStatus.vehicleNumber,
              salesmanId: routeStatus.salesmanId,
              salesmanName: salesmanName,
            };
            assignedVehicleIds.add(routeStatus.vehicleId);
            console.log('✅ Added route assignment from route_daily_status:', {
              routeId: routeStatus.routeId,
              assignment: assignments[routeStatus.routeId]
            });
          }
        }
      });
      setVehicleToRoute(mapping);
      setRouteAssignments(assignments);

      // Also populate route statuses directly from the route_daily_status data
      const routeStatusesMap: {[routeId: string]: any} = {};
      routeDailyStatusSnapshot.docs.forEach(d => {
        const routeStatus: any = d.data();
        if (routeStatus.routeId) {
          routeStatusesMap[routeStatus.routeId] = {
            id: d.id,
            ...routeStatus
          };
        }
      });
      setRouteStatuses(routeStatusesMap);
      console.log('🔍 DEBUG: Route statuses map:', routeStatusesMap);

      // Load only vehicles that have route assignments for the selected date
      const vehiclesSnapshot = await getDocs(
        query(
          collection(db, 'vehicles'), 
          where('warehouseId', '==', selectedWarehouseDoc.warehouseId),
          where('isActive', '==', true)
        )
      );
      const allVehiclesData = vehiclesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Vehicle));
      
      // Filter vehicles to only those with assignments for the selected date
      const vehiclesData = allVehiclesData.filter(vehicle => 
        assignedVehicleIds.has(vehicle.vehicleId)
      );
      
      console.log('🚛 Found vehicles for settlement:', vehiclesData.length, vehiclesData.map(v => v.vehicleNumber));
      console.log('📅 Date-filtered vehicles (assigned on', selectedDate, '):', vehiclesData.map(v => `${v.vehicleNumber} -> Route:${mapping[v.vehicleId]} -> Salesman:${assignments[mapping[v.vehicleId]]?.salesmanName || 'None'}`));
      setVehicles(vehiclesData);

      // Load route inventory per vehicle via mapped routeId
      const vehicleInventoryData: {[vehicleId: string]: {[productId: string]: number}} = {};
      for (const vehicle of vehiclesData) {
        vehicleInventoryData[vehicle.vehicleId] = {};
        const rid = mapping[vehicle.vehicleId];
        if (!rid) continue;
        const routeInvSnapshot = await getDocs(
          query(
            collection(db, 'inventory'),
            where('warehouseId', '==', rid)
          )
        );
        routeInvSnapshot.docs.forEach(doc => {
          const invData = doc.data() as any;
          vehicleInventoryData[vehicle.vehicleId][invData.productId] = invData.closingStock || 0;
        });
      }
      setVehicleInventory(vehicleInventoryData);
      
    } catch (error) {
      console.error('Error loading warehouse data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVehicleStatuses = async () => {
    try {
      if (vehicles.length === 0) return;
      
      // 🔧 IMPROVEMENT: Set up real-time listeners for vehicle status changes
      const unsubscribers: (() => void)[] = [];
      
      for (const vehicle of vehicles) {
        try {
          const rid = vehicleToRoute[vehicle.vehicleId];
          if (!rid) continue;
          const statusQuery = query(
            collection(db, 'route_daily_status'),
            where('routeId', '==', rid),
            where('date', '==', selectedDate)
          );
          // Set up real-time listener for each route status
          const unsubscribe = onSnapshot(statusQuery, (snapshot) => {
            if (snapshot.docs.length > 0) {
              const status = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as any;
              setVehicleStatuses(prevStatuses => ({
                ...prevStatuses,
                [vehicle.vehicleId]: status
              }));
            } else {
              setVehicleStatuses(prevStatuses => {
                const newStatuses = { ...prevStatuses };
                delete newStatuses[vehicle.vehicleId];
                return newStatuses;
              });
            }
          }, (error) => {
            console.error(`Error in real-time listener for route of vehicle ${vehicle.id}:`, error);
          });
          unsubscribers.push(unsubscribe);
        } catch (error) {
          console.error(`Error setting up route status listener for vehicle ${vehicle.id}:`, error);
        }
      }
      
      // Store unsubscribers for cleanup
      return () => {
        unsubscribers.forEach(unsubscribe => unsubscribe());
      };
      
    } catch (error) {
      console.error('Error loading vehicle statuses:', error);
    }
  };

  const loadVehicleData = async () => {
    try {
      if (!currentUser?.id) {
        return;
      }

      // Get ALL sales for the date (warehouse user needs visibility of all sales)
      const salesHeadersSnapshot = await getDocs(
        query(
          collection(db, 'salesHeaders'),
          where('date', '==', selectedDate),
          where('status', '==', 'completed')
        )
      );

      const allSalesHeaders = salesHeadersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get ALL sales details
      const allSalesDetails: any[] = [];
      for (const header of allSalesHeaders) {
        const salesDetailsSnapshot = await getDocs(
          query(
            collection(db, 'salesDetails'),
            where('transactionId', '==', (header as any).transactionId)
          )
        );

        const transactionDetails = salesDetailsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        allSalesDetails.push(...transactionDetails);
      }

      // Now build route-specific sales data (route-first; independent of beats/vehicles)
      const salesData: {[vehicleId: string]: {
        salesHeaders: any[];
        salesDetails: any[];
        productSummary: {[productId: string]: {sales: number; replace: number}};
      }} = {};
      const routeSales: {[routeId: string]: {
        salesHeaders: any[];
        salesDetails: any[];
        productSummary: {[productId: string]: {sales: number; replace: number}};
      }} = {};
      
      // Group sales directly by routeId for all routes in this warehouse
      const wh = warehouses.find(w => w.id === selectedWarehouse);
      const warehouseCustomId = wh?.warehouseId || selectedWarehouse;
      const routeIdsInWarehouse = routes
        .filter(r => (r as any).warehouseId === warehouseCustomId)
        .map(r => (r as any).routeId)
        .filter(Boolean) as string[];

      // Pre-index details by transactionId for efficient grouping
      const detailsByTxn = new Map<string, any[]>();
      allSalesDetails.forEach((d: any) => {
        const arr = detailsByTxn.get(d.transactionId) || [];
        arr.push(d);
        detailsByTxn.set(d.transactionId, arr);
      });

      for (const rid of routeIdsInWarehouse) {
        const headersForRoute = allSalesHeaders.filter((h: any) => h.routeId === rid);
        const txnIds = headersForRoute.map((h: any) => h.transactionId);
        const detailsForRoute: any[] = [];
        txnIds.forEach((id: string) => {
          const arr = detailsByTxn.get(id);
          if (arr && arr.length) detailsForRoute.push(...arr);
        });

        const productSummary: {[productId: string]: {sales: number; replace: number}} = {};
        detailsForRoute.forEach((detail: any) => {
          if (!productSummary[detail.productId]) productSummary[detail.productId] = { sales: 0, replace: 0 };
          productSummary[detail.productId].sales += detail.saleQty || 0;
          productSummary[detail.productId].replace += detail.replaceQty || 0;
        });

        routeSales[rid] = {
          salesHeaders: headersForRoute,
          salesDetails: detailsForRoute,
          productSummary
        };
      }
      
  setVehicleSalesData(salesData); // Retained for backward compatibility
  setRouteSalesData(routeSales);
      
    } catch (error) {
      console.error('Error loading vehicle data:', error);
    }
  };

  const loadVehicleSettlementData = async () => {
    if (!activeRouteTab) return;
    
    try {
      // Load products collection to get product details
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];
      
      // 🔧 FIX: Debug products data structure
      console.log('🔍 DEBUG: Raw products data sample:', productsData.slice(0, 2));

      // Get opening stock from route_daily_status for mapped route
  const rid = activeRouteTab;
      let openingStockData: {[productId: string]: number} = {};
      try {
        if (rid) {
          const routeStatusSnapshot = await getDocs(
            query(
              collection(db, 'route_daily_status'),
              where('routeId', '==', rid),
              where('date', '==', selectedDate)
            )
          );
          if (routeStatusSnapshot.docs.length > 0) {
            const routeStatus = routeStatusSnapshot.docs[0].data() as any;
            if (routeStatus.detailedAllocations && routeStatus.detailedAllocations.length > 0) {
                routeStatus.detailedAllocations.forEach((allocation: any) => {
                  // openingQty is pre-issue carry-over; effective opening for the day = carryOver + issuedQty
                  const carryOver = Number(allocation.openingQty || 0);
                  const issued = Number(allocation.issuedQty || 0);
                  const finalOpening = carryOver + issued;
                  if (finalOpening > 0) {
                    openingStockData[allocation.productId] = finalOpening;
                  }
                });
            }
          }
        }
      } catch (error) {
        console.error('Error loading route daily status:', error);
      }

      // Fallback to current route inventory if no opening stock found
        // No fallback: rely solely on detailedAllocations for opening stock
      
  // Get route sales data from the salesHeaders/salesDetails structure
  const currentVehicleSalesData = routeSalesData[activeRouteTab];
      const productSummary = currentVehicleSalesData?.productSummary || {};
      const salesHeaders = currentVehicleSalesData?.salesHeaders || [];
      
      // Calculate settlements for each product
      const settlements: ProductSettlement[] = [];
      
      // Get all products that have opening stock OR have sales data
      const allProductIds = new Set([
        ...Object.keys(openingStockData),
        ...Object.keys(productSummary)
      ]);

      allProductIds.forEach((productId) => {
        const openingStock = openingStockData[productId] || 0;
        const salesData = productSummary[productId] || { sales: 0, replace: 0 };
        
        // Only include products that have opening stock or sales activity
        if (openingStock > 0 || salesData.sales > 0 || salesData.replace > 0) {
          // 🔧 FIX: Handle both product mapping scenarios - same as InventoryManagement
          const product = productsData.find((p: any) => {
            // Try both productId field and document ID
            return p.productId === productId || p.id === productId;
          });
          
          console.log('🔍 DEBUG: Product lookup for', productId, {
            foundProduct: !!product,
            productDetails: product ? {
              documentId: product.id,
              productIdField: product.productId,
              name: product.name,
              productName: product.productName,
              finalName: product?.name || product?.productName || `Unknown Product (${productId})`
            } : null
          });
          
          const settlement: ProductSettlement = {
            productId,
            productName: product?.name || product?.productName || `Unknown Product (${productId})`,
            unit: product?.unit || 'KG',
            category: product?.category || product?.categoryName || 'Unknown',
            openingStock, // Now from detailedAllocations (actual loadout)
            sales: salesData.sales,
            replace: salesData.replace, // From salesDetails.replaceQty - damaged/returned items
            currentStock: openingStock - salesData.sales - salesData.replace, // Fix: Replace items are REMOVED
            physicalStock: 0, // Will be updated by user input
            variance: 0 // Will be calculated when physicalStock is entered
          };
          
          settlements.push(settlement);
        }
      });
      
      setProductSettlements(settlements);
      
      // Calculate cash and credit totals from salesHeaders
      const totalCash = salesHeaders
        .filter((header: any) => header.txnMode === 'cash')
        .reduce((total: number, header: any) => total + (header.netAmt || 0), 0);
      
      const totalCredit = salesHeaders
        .filter((header: any) => header.txnMode === 'credit')
        .reduce((total: number, header: any) => total + (header.netAmt || 0), 0);
      
      const creditInvoices = salesHeaders
        .filter((header: any) => header.txnMode === 'credit')
        .map((header: any) => header.transactionId);
      
      setCashCredit({
        totalCashSale: totalCash,
        amountReceived: 0, // To be entered by user
        creditSale: totalCredit,
        invoiceList: creditInvoices
      });
      
    } catch (error) {
      console.error('Error loading vehicle settlement data:', error);
    }
  };

  const updatePhysicalStock = (productId: string, physicalStock: number) => {
    setProductSettlements(prev => 
      prev.map(settlement => {
        if (settlement.productId === productId) {
          const variance = physicalStock - settlement.currentStock;
          return {
            ...settlement,
            physicalStock,
            variance
          };
        }
        return settlement;
      })
    );
  };

  const updateReplaceQuantity = (productId: string, replace: number) => {
    setProductSettlements(prev => 
      prev.map(settlement => {
        if (settlement.productId === productId) {
          const currentStock = settlement.openingStock - settlement.sales + replace;
          const variance = settlement.physicalStock - currentStock;
          return {
            ...settlement,
            replace,
            currentStock,
            variance
          };
        }
        return settlement;
      })
    );
  };

  const updateCashReceived = (amount: number) => {
    setCashCredit(prev => ({
      ...prev,
      amountReceived: amount
    }));
  };

  // Helper function to determine if settlement is allowed based on date and route status
  const canSettle = (routeStatus: any, settlementDate: string) => {
    if (!routeStatus?.salesmanId && !routeStatus?.salesmanUserId) return false; // No route assigned
    
    const today = new Date().toISOString().split('T')[0];
    
    if (settlementDate === today) {
      // Current day - only salesman can settle (must be in 'settling' status)
      return routeStatus.status === 'settling';
    } else {
      // Previous day - warehouse can force settlement (anything not 'settled')
      return routeStatus.status !== 'settled' && currentUser?.role !== 'salesman';
    }
  };

  const handleSettle = async () => {
    try {
      setSettling(true);
      
  const assign = routeAssignments[activeRouteTab] || {};
  const vehicle = assign.vehicleId ? vehicles.find(v => v.vehicleId === assign.vehicleId) : undefined;
      const warehouse = warehouses.find(w => w.id === selectedWarehouse);
      
      if (!vehicle) {
        alert('Vehicle not found!');
        return;
      }

      if (!warehouse) {
        alert('Warehouse not found!');
        return;
      }

      // Validate settlement data
      const hasVariances = productSettlements.some(p => p.variance !== 0);
      const cashVariance = cashCredit.totalCashSale - cashCredit.amountReceived;
      
      if (hasVariances || cashVariance !== 0) {
        const confirmMessage = `Settlement Discrepancies Detected:

${hasVariances ? 'Stock Variances Found:\n' + 
  productSettlements
    .filter(p => p.variance !== 0)
    .map(p => `${p.productName}: ${p.variance > 0 ? '+' : ''}${p.variance}`)
    .join('\n') + '\n' : ''
}${cashVariance !== 0 ? `Cash Variance: ₹${cashVariance}\n` : ''}
Do you want to proceed with settlement despite these discrepancies?`;

        if (!window.confirm(confirmMessage)) {
          return;
        }
      }

      const timestamp = serverTimestamp();
      const settlementId = `STL-${Date.now()}-${vehicle.vehicleNumber}`;

      console.log('🔄 Starting settlement process using unified transactions...');

      // Phase 1: Return remaining physical stock to parent warehouse
      const returnItems = productSettlements
        .filter(settlement => settlement.physicalStock > 0)
        .map(settlement => ({
          productId: settlement.productId,
          productName: settlement.productName,
          unit: settlement.unit,
          quantity: settlement.physicalStock
        }));

      if (returnItems.length > 0) {
        console.log('🔄 Processing stock returns to warehouse:', returnItems);
        const returnResult = await UnifiedInventoryTransactionService.processSettlementTransaction(
          vehicleToRoute[activeVehicleTab],        // FROM route
          warehouse.warehouseId,    // TO parent warehouse
          returnItems,
          settlementId,
          currentUser?.id || ''
        );

        if (!returnResult.success) {
          console.error('Failed to process stock returns:', returnResult.errors);
          alert('Failed to process stock returns. Please try again.');
          return;
        }
        console.log('✅ Stock returns processed successfully:', returnResult.journalIds.length, 'transactions');
      }

      // Phase 2: Handle variances through Stock Gain Loss WH
      const shortageItems = productSettlements
        .filter(settlement => settlement.variance < 0)
        .map(settlement => ({
          productId: settlement.productId,
          productName: settlement.productName,
          unit: settlement.unit,
          quantity: Math.abs(settlement.variance)
        }));

      const excessItems = productSettlements
        .filter(settlement => settlement.variance > 0)
        .map(settlement => ({
          productId: settlement.productId,
          productName: settlement.productName,
          unit: settlement.unit,
          quantity: settlement.variance
        }));

      // Handle shortages (FROM Stock Gain Loss WH TO route to balance books)
      if (shortageItems.length > 0) {
        console.log('🔄 Processing stock shortages:', shortageItems);
        const shortageResult = await UnifiedInventoryTransactionService.processSettlementTransaction(
          'STOCK-GAIN-LOSS-WH',     // FROM Stock Gain Loss WH
          vehicleToRoute[activeVehicleTab],        // TO route (to balance shortage)
          shortageItems,
          settlementId,
          currentUser?.id || ''
        );

        if (!shortageResult.success) {
          console.error('Failed to process stock shortages:', shortageResult.errors);
          alert('Failed to process stock shortages. Please try again.');
          return;
        }
        console.log('✅ Stock shortages processed successfully:', shortageResult.journalIds.length, 'transactions');
      }

      // Handle excess (FROM route TO Stock Gain Loss WH)
      if (excessItems.length > 0) {
        console.log('🔄 Processing stock excess:', excessItems);
        const excessResult = await UnifiedInventoryTransactionService.processSettlementTransaction(
          vehicleToRoute[activeVehicleTab],        // FROM route
          'STOCK-GAIN-LOSS-WH',     // TO Stock Gain Loss WH
          excessItems,
          settlementId,
          currentUser?.id || ''
        );

        if (!excessResult.success) {
          console.error('Failed to process stock excess:', excessResult.errors);
          alert('Failed to process stock excess. Please try again.');
          return;
        }
        console.log('✅ Stock excess processed successfully:', excessResult.journalIds.length, 'transactions');
      }

      // Build aggregated sales and replacement records for ERP sync (Option A)
      // We aggregate at route/day level into two pseudo RetailerSale entries: Daily Cash Sales and Daily Replacements
      const salesAggItems: { productId: string; productName: string; unit: string; quantity: number; rate: number; totalAmount: number; tax: number; taxAmount: number }[] = [];
      const replaceAggItems: { productId: string; productName: string; unit: string; quantity: number; rate: number; totalAmount: number; tax: number; taxAmount: number }[] = [];

      // For per-item rates, try to compute weighted average from actual salesDetails for this route/date
      const currentRouteSales = routeSalesData[activeRouteTab];
      const detailsForRoute = currentRouteSales?.salesDetails || [];
      // Index details by productId for quick aggregation
      const detailsByProduct = new Map<string, { saleQty: number; replaceQty: number; amount: number; unit?: string }>();
      for (const d of detailsForRoute) {
        const pid = d.productId;
        const saleQty = Number(d.saleQty || 0);
        const replaceQty = Number(d.replaceQty || 0);
        const rate = Number(d.unitRate || 0);
        const amount = Number(d.lineTotal || saleQty * rate || 0);
        const prev = detailsByProduct.get(pid) || { saleQty: 0, replaceQty: 0, amount: 0, unit: d.unit || 'Pckts' };
        prev.saleQty += saleQty;
        prev.replaceQty += replaceQty;
        prev.amount += amount;
        if (!prev.unit && d.unit) prev.unit = d.unit;
        detailsByProduct.set(pid, prev);
      }

      // Now traverse productSettlements to form line items (ensures we include products that had replace without sale)
      for (const p of productSettlements) {
        const pid = p.productId;
        const prodName = p.productName;
        const unit = (p.unit || 'Pckts').trim();
        const salesQty = Number(p.sales || 0);
        const replaceQty = Number(p.replace || 0);

        if (salesQty > 0) {
          const det = detailsByProduct.get(pid);
          const rate = det && det.saleQty > 0 ? det.amount / det.saleQty : 0;
          const totalAmount = rate * salesQty;
          salesAggItems.push({
            productId: pid,
            productName: prodName,
            unit,
            quantity: salesQty,
            rate,
            totalAmount,
            tax: 0,
            taxAmount: 0,
          });
        }

        if (replaceQty > 0) {
          // Treat replacements as zero-value returns by default; rate kept for reporting
          const det = detailsByProduct.get(pid);
          const rate = det && det.saleQty > 0 ? det.amount / det.saleQty : 0;
          const totalAmount = rate * replaceQty;
          replaceAggItems.push({
            productId: pid,
            productName: prodName,
            unit,
            quantity: replaceQty,
            rate,
            totalAmount,
            tax: 0,
            taxAmount: 0,
          });
        }
      }

      // Build pseudo RetailerSale entries
      const dailySalesRecord = salesAggItems.length > 0 ? {
        id: `RS-${Date.now()}-${vehicle.vehicleNumber}-SALES`,
        saleId: `DAILY-${selectedDate}-SALES`,
        routeId: vehicleToRoute[activeVehicleTab],
        routeName: routes.find((r: any) => r.routeId === vehicleToRoute[activeVehicleTab])?.name || vehicleToRoute[activeVehicleTab],
        salesmanId: currentUser?.id || '',
        salesmanName: currentUser?.email || 'Unknown',
        beatId: `BEAT-${selectedDate}-${vehicle.vehicleNumber}`,
        retailerName: 'Daily Cash Sales',
        saleDate: selectedDate,
        saleTime: new Date().toISOString(),
        items: salesAggItems.map(it => ({
          productId: it.productId,
          productName: it.productName,
          unit: it.unit,
          quantity: it.quantity,
          rate: it.rate,
          totalAmount: it.totalAmount,
          tax: it.tax,
          taxAmount: it.taxAmount,
        })),
        subtotal: salesAggItems.reduce((s, it) => s + it.totalAmount, 0),
        taxAmount: 0,
        totalAmount: salesAggItems.reduce((s, it) => s + it.totalAmount, 0),
        paymentMethod: 'cash' as const,
        status: 'completed' as const,
        syncedWithERP: false,
        createdBy: currentUser?.id || '',
      } : undefined;

      const dailyReplaceRecord = replaceAggItems.length > 0 ? {
        id: `RS-${Date.now()}-${vehicle.vehicleNumber}-REPLACE`,
        saleId: `DAILY-${selectedDate}-REPLACE`,
        routeId: vehicleToRoute[activeVehicleTab],
  routeName: routes.find((r: any) => r.routeId === vehicleToRoute[activeVehicleTab])?.name || vehicleToRoute[activeVehicleTab],
        salesmanId: currentUser?.id || '',
        salesmanName: currentUser?.email || 'Unknown',
        beatId: `BEAT-${selectedDate}-${vehicle.vehicleNumber}`,
        retailerName: 'Daily Replacements',
        saleDate: selectedDate,
        saleTime: new Date().toISOString(),
        items: replaceAggItems.map(it => ({
          productId: it.productId,
          productName: it.productName,
          unit: it.unit,
          quantity: it.quantity,
          rate: it.rate,
          totalAmount: it.totalAmount,
          tax: it.tax,
          taxAmount: it.taxAmount,
          replacementRequired: true,
        })),
        subtotal: replaceAggItems.reduce((s, it) => s + it.totalAmount, 0),
        taxAmount: 0,
        totalAmount: replaceAggItems.reduce((s, it) => s + it.totalAmount, 0),
        paymentMethod: 'cash' as const,
        status: 'completed' as const,
        syncedWithERP: false,
        createdBy: currentUser?.id || '',
      } : undefined;

      // Phase 3: Create settlement record
      const settlementData: Omit<SettlementData, 'id'> = {
        settlementId,
        salesmanId: currentUser?.id || '',
        salesmanName: currentUser?.email || 'Unknown',
        routeId: vehicleToRoute[activeVehicleTab],
        routeName: routes.find((r: any) => r.routeId === vehicleToRoute[activeVehicleTab])?.name || vehicleToRoute[activeVehicleTab],
  parentWarehouseId: warehouse.warehouseId,
        parentWarehouseName: warehouse.name,
        beatId: `BEAT-${selectedDate}-${vehicle.vehicleNumber}`,
        settlementDate: selectedDate,
        
        // Simplified data structure
        allocatedItems: [],
        salesRecords: dailySalesRecord ? [dailySalesRecord] as any : [],
        // Store replacements separately for clarity; orchestrator can treat them as replace lines
        replaceRecords: dailyReplaceRecord ? [dailyReplaceRecord] as any : [],
        damageRecords: [],
        returnedStock: returnItems.map(ri => ({
          productId: ri.productId,
          productName: ri.productName,
          unit: ri.unit,
          availableStock: 0,
          allocatedQty: ri.quantity,
          rate: 0,
          totalValue: 0,
        })),
        
        // Cash reconciliation
        expectedCash: cashCredit.totalCashSale,
        actualCash: cashCredit.amountReceived,
        variance: cashVariance,
        ...(hasVariances || cashVariance !== 0 ? { 
          varianceReason: 'Discrepancies noted during settlement' 
        } : {}),
        
        // Settlement summary
        totalItemsAllocated: productSettlements.reduce((sum, p) => sum + p.openingStock, 0),
        totalItemsSold: productSettlements.reduce((sum, p) => sum + p.sales, 0),
        totalItemsDamaged: productSettlements.reduce((sum, p) => sum + Math.max(0, -p.variance), 0),
        totalItemsReturned: productSettlements.reduce((sum, p) => sum + Math.max(0, p.variance), 0),
        
        // Settlement status
        status: (hasVariances || cashVariance !== 0) ? 'discrepancy' : 'completed',
        reconciledAt: timestamp,
        completedAt: (hasVariances || cashVariance !== 0) ? undefined : timestamp,
        syncedWithZoho: false,
        
        createdAt: timestamp,
        updatedAt: timestamp,
        createdBy: currentUser?.id || '',
        updatedBy: currentUser?.id || ''
      };

      // Create settlement document
      const settlementRef = doc(collection(db, 'settlements'));
      await setDoc(settlementRef, settlementData);

      // Phase 4: Update route daily status
      const rid = vehicleToRoute[activeVehicleTab];
      if (rid) {
        const statusRef = doc(db, 'route_daily_status', `${rid}_${selectedDate}`);
        await setDoc(statusRef, {
          status: 'settled',
          settledAt: timestamp,
          settledBy: currentUser?.id || '',
          updatedAt: timestamp
        }, { merge: true });
      }

      console.log('✅ Settlement completed successfully');

      alert(`✅ Settlement completed for ${vehicle.vehicleNumber}!

Settlement ID: ${settlementId}
Status: ${settlementData.status}

Inventory movements processed via unified transaction system:
${returnItems.length > 0 ? `• ${returnItems.length} products returned to ${warehouse.name}\n` : ''}${shortageItems.length > 0 ? `• ${shortageItems.length} stock shortages balanced via Stock Gain Loss WH\n` : ''}${excessItems.length > 0 ? `• ${excessItems.length} excess stock moved to Stock Gain Loss WH\n` : ''}${cashVariance !== 0 ? `• Cash variance: ₹${cashVariance}\n` : ''}
• Vehicle status updated to settled`);

      // Auto-detect next earliest unsettled date after settlement completion
      const selectedWarehouseObj = warehouses.find(w => w.id === selectedWarehouse);
      if (selectedWarehouseObj) {
        const warehouseCustomId = selectedWarehouseObj.warehouseId || selectedWarehouse;
        await detectEarliestUnsettledDate(warehouseCustomId);
      }
      
      // Refresh data for the new date
      await loadWarehouseData();

    } catch (error) {
      console.error('Error settling vehicle:', error);
      alert(`Error settling vehicle: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setSettling(false);
    }
  };

  const selectedWarehouseName = warehouses.find(w => w.id === selectedWarehouse)?.name || '';
  const activeVehicleName = (() => {
    const assign = routeAssignments[activeRouteTab] || {};
    return assign.vehicleName || '';
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading settlement interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Title and Back Button */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={onBack}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back</span>
                </button>
                <div className="flex items-center space-x-3">
                  <Calculator className="w-6 h-6 text-green-600" />
                  <div>
                    <h1 className="text-xl font-semibold text-gray-800">Settle Route</h1>
                    {settlementInfo && (
                      <div className={`text-sm mt-1 ${
                        settlementInfo.routeCount > 0 
                          ? 'text-orange-600 font-medium' 
                          : 'text-gray-600'
                      }`}>
                        {settlementInfo.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Summary Stats */}
              <div className="flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {productSettlements.length}
                  </div>
                  <div className="text-gray-600">Products</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">
                    ₹{(cashCredit.totalCashSale + cashCredit.creditSale).toLocaleString()}
                  </div>
                  <div className="text-gray-600">Total Sales</div>
                </div>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Warehouse Selector */}
                <div className="min-w-64">
                  <select
                    value={selectedWarehouse}
                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Date Display */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Date: {selectedDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!selectedWarehouse ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">Select a Warehouse</h3>
            <p className="text-gray-600">Choose a warehouse to start settlement process</p>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-6">
            
            {/* Route Tabs (with salesman and vehicle sub-tags) */}
            {routes.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">Route Selection</h2>
                  <p className="text-sm text-gray-600">{selectedWarehouseName}</p>
                  
                  {/* Route Tabs with Status Indicators and sub-tags */}
                  <div className="mt-4 flex flex-wrap gap-2 bg-gray-100 rounded-lg p-1">
                    {routes.map((route) => {
                      const rid = (route as any).routeId as string;
                      if (!rid) return null;
                      const statusObj = routeStatuses[rid];
                      const status = statusObj?.status;
                      const isActive = activeRouteTab === rid;
                      const assign = routeAssignments[rid] || {};
                      const subline = (
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                          {assign.salesmanName ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full border border-gray-200">
                              <User className="w-3 h-3"/> {assign.salesmanName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full border border-gray-200">
                              <User className="w-3 h-3"/> No Salesman
                            </span>
                          )}
                          {assign.vehicleName ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full border border-gray-200">
                              <Truck className="w-3 h-3"/> {assign.vehicleName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full border border-gray-200">
                              <Truck className="w-3 h-3"/> No Vehicle
                            </span>
                          )}
                        </div>
                      );
                      return (
                        <button
                          key={rid}
                          onClick={() => { setActiveRouteTab(rid); if (assign.vehicleId) setActiveVehicleTab(assign.vehicleId); }}
                          className={`relative px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <span>{(route as any).name || (route as any).routeName || rid}</span>
                            <div className={`w-2 h-2 rounded-full ${
                              status === 'on_field' ? 'bg-blue-500' :
                              status === 'settling' ? 'bg-orange-500' :
                              status === 'settled' ? 'bg-green-500' :
                              'bg-gray-400'
                            }`} />
                          </div>
                          {subline}
                        </button>
                      );
                    })}
                  </div>

                  {/* Status Legend */}
                  <div className="mt-3 flex items-center space-x-4 text-xs text-gray-600">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>On Field</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>Ready for Settlement</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Settled</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Physical Stock Verification - ALWAYS SHOW */}
            {activeRouteTab && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800">Physical Stock Verification</h2>
                      <p className="text-sm text-gray-600">{routes.find(r => (r as any).routeId === activeRouteTab)?.name || activeRouteTab}</p>
                      {/* Status-based editing indicator */}
                      {(() => {
                        const routeStatus = routeStatuses[activeRouteTab];
                        const status = routeStatus?.status;
                        if (status === 'on_field') {
                          const today = new Date().toISOString().split('T')[0];
                          const isCurrentDay = selectedDate === today;
                          
                          return (
                            <div className="flex items-center space-x-2 mt-1">
                              <Clock className="w-4 h-4 text-blue-500" />
                              <span className="text-sm text-blue-600">
                                {isCurrentDay 
                                  ? 'Waiting for salesman to mark ready for settlement'
                                  : 'Previous day - Warehouse can force settlement'}
                              </span>
                            </div>
                          );
                        } else if (status === 'settled') {
                          return (
                            <div className="flex items-center space-x-2 mt-1">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-600">Settlement completed - Read only</span>
                            </div>
                          );
                        } else if (status === 'settling') {
                          return (
                            <div className="flex items-center space-x-2 mt-1">
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                              <span className="text-sm text-orange-600">Ready for settlement - Editable</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="text-sm text-blue-600">
                      {productSettlements.length} products loaded
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Category</th>
                          <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Product</th>
                          <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">Opening Stock</th>
                          <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">Sales</th>
                          <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">Replace</th>
                          <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">Current Stock</th>
                          <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">Physical Stock</th>
                          <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productSettlements.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                              <div className="space-y-2">
                                <Package className="w-12 h-12 mx-auto text-gray-300" />
                                <p className="font-medium">No inventory data found for this vehicle</p>
                                <p className="text-sm">
                                  This could mean:
                                  <br />• No products have been loaded to this vehicle today
                                  <br />• Vehicle inventory data is not available
                                  <br />• No sales transactions recorded yet
                                </p>
                                <button
                                  onClick={() => {
                                    loadVehicleSettlementData();
                                  }}
                                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                                >
                                  <RefreshCw className="w-4 h-4 inline mr-2" />
                                  Retry Loading Data
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          productSettlements.map((settlement) => (
                            <tr key={settlement.productId} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-2 text-gray-600">{settlement.category}</td>
                              <td className="border border-gray-300 px-4 py-2 text-gray-800 font-medium">{settlement.productName}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center font-medium text-blue-600">{settlement.openingStock}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center font-medium text-red-600">{settlement.sales}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">
                                {(() => {
                                  const routeStatus = routeStatuses[activeRouteTab];
                                  const canSettleItem = (() => {
                                    if (!routeStatus?.salesmanId && !routeStatus?.salesmanUserId) return false; // No route assigned
                                    
                                    const today = new Date().toISOString().split('T')[0];
                                    if (selectedDate === today) {
                                      // Current day - only salesman can settle (must be in 'settling' status)
                                      return routeStatus.status === 'settling';
                                    } else {
                                      // Previous day - warehouse can force settlement (anything not 'settled')
                                      return routeStatus.status !== 'settled' && currentUser?.role !== 'salesman';
                                    }
                                  })();
                                  
                                  return (
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={settlement.replace === 0 ? '' : settlement.replace}
                                      placeholder="0"
                                      disabled={!canSettleItem}
                                      onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                          e.preventDefault();
                                        }
                                      }}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                                        updateReplaceQuantity(settlement.productId, numValue);
                                      }}
                                      className={`w-16 px-2 py-1 border rounded text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                        canSettleItem 
                                          ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                                          : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                      }`}
                                    />
                                  );
                                })()}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">{settlement.currentStock}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">
                                {(() => {
                                  const routeStatus = routeStatuses[activeRouteTab];
                                  const canSettle = (() => {
                                    if (!routeStatus?.salesmanId && !routeStatus?.salesmanUserId) return false; // No route assigned
                                    
                                    const today = new Date().toISOString().split('T')[0];
                                    if (selectedDate === today) {
                                      // Current day - only salesman can settle (must be in 'settling' status)
                                      return routeStatus.status === 'settling';
                                    } else {
                                      // Previous day - warehouse can force settlement (anything not 'settled')
                                      return routeStatus.status !== 'settled' && currentUser?.role !== 'salesman';
                                    }
                                  })();
                                  
                                  console.log('🔍 Physical Stock canSettle debug:', {
                                    routeId: activeRouteTab,
                                    selectedDate,
                                    today: new Date().toISOString().split('T')[0],
                                    routeStatus: routeStatus,
                                    salesmanId: routeStatus?.salesmanId,
                                    currentUserRole: currentUser?.role,
                                    canSettle: canSettle
                                  });
                                  
                                  return (
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={settlement.physicalStock === 0 ? '' : settlement.physicalStock}
                                      placeholder="0"
                                      disabled={!canSettle}
                                      onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                          e.preventDefault();
                                        }
                                      }}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                                        updatePhysicalStock(settlement.productId, numValue);
                                      }}
                                      className={`w-16 px-2 py-1 border rounded text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                        canSettle 
                                          ? 'border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent' 
                                          : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                      }`}
                                    />
                                  );
                                })()}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-center">
                                <span className={`font-medium ${
                                  settlement.variance === 0 ? 'text-green-600' :
                                  settlement.variance > 0 ? 'text-blue-600' : 'text-red-600'
                                }`}>
                                  {settlement.variance > 0 ? '+' : ''}{settlement.variance}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                        {/* Total Row */}
                        {productSettlements.length > 0 && (
                          <tr className="bg-gray-100 font-semibold">
                            <td className="border border-gray-300 px-4 py-2 text-gray-800" colSpan={2}>Total</td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-gray-800">
                              {productSettlements.reduce((sum, p) => sum + p.openingStock, 0)}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-gray-800">
                              {productSettlements.reduce((sum, p) => sum + p.sales, 0)}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-gray-800">
                              {productSettlements.reduce((sum, p) => sum + p.replace, 0)}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-gray-800">
                              {productSettlements.reduce((sum, p) => sum + p.currentStock, 0)}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-gray-800">
                              {productSettlements.reduce((sum, p) => sum + p.physicalStock, 0)}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-gray-800">
                              {productSettlements.reduce((sum, p) => sum + p.variance, 0)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cash and Credit Sales Verification */}
            {activeRouteTab && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">Cash and Credit Sales Verification</h2>
                  <p className="text-sm text-gray-600">{routes.find(r => (r as any).routeId === activeRouteTab)?.name || activeRouteTab} {activeVehicleName ? `• ${activeVehicleName}` : ''}</p>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Total Cash Sale */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-3">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <div>
                        <div className="font-medium text-gray-800">Total Cash Sale:</div>
                        <div className="text-sm text-gray-600">Expected amount from cash sales</div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      ₹{cashCredit.totalCashSale.toLocaleString()}
                    </div>
                  </div>

                  {/* Amount Received */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="font-medium text-gray-800">Amount Received:</div>
                        <div className="text-sm text-gray-600">Physical cash collected</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">₹</span>
                      {(() => {
                        const routeStatus = routeStatuses[activeRouteTab];
                        const isEditable = canSettle(routeStatus, selectedDate);
                        
                        return (
                          <input
                            type="text"
                            inputMode="numeric"
                            value={cashCredit.amountReceived === 0 ? '' : cashCredit.amountReceived}
                            placeholder="0"
                            disabled={!isEditable}
                            onKeyPress={(e) => {
                              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              const numValue = value === '' ? 0 : parseInt(value) || 0;
                              updateCashReceived(numValue);
                            }}
                            className={`w-32 px-3 py-2 border rounded text-right font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                              isEditable 
                                ? 'border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                                : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                            }`}
                          />
                        );
                      })()}
                    </div>
                  </div>

                  {/* Cash Variance */}
                  {(cashCredit.totalCashSale - cashCredit.amountReceived) !== 0 && (
                    <div className={`flex items-center justify-between p-3 rounded-lg border ${
                      (cashCredit.totalCashSale - cashCredit.amountReceived) > 0 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center space-x-3">
                        <AlertTriangle className={`w-5 h-5 ${
                          (cashCredit.totalCashSale - cashCredit.amountReceived) > 0 
                            ? 'text-red-600' 
                            : 'text-yellow-600'
                        }`} />
                        <div>
                          <div className="font-medium text-gray-800">Cash Variance:</div>
                          <div className="text-sm text-gray-600">
                            {(cashCredit.totalCashSale - cashCredit.amountReceived) > 0 
                              ? 'Cash shortage detected' 
                              : 'Excess cash received'}
                          </div>
                        </div>
                      </div>
                      <div className={`text-lg font-bold ${
                        (cashCredit.totalCashSale - cashCredit.amountReceived) > 0 
                          ? 'text-red-600' 
                          : 'text-yellow-600'
                      }`}>
                        {(cashCredit.totalCashSale - cashCredit.amountReceived) > 0 ? '-' : '+'}₹{Math.abs(cashCredit.totalCashSale - cashCredit.amountReceived).toLocaleString()}
                      </div>
                    </div>
                  )}

                  {/* Credit Sale */}
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="w-5 h-5 text-purple-600" />
                      <div>
                        <div className="font-medium text-gray-800">Credit Sale:</div>
                        <div className="text-sm text-gray-600">Outstanding amounts from credit sales</div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-purple-600">
                      ₹{cashCredit.creditSale.toLocaleString()}
                    </div>
                  </div>

                  {/* Credit Invoices */}
                  {cashCredit.invoiceList.length > 0 && (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <FileText className="w-4 h-4 text-gray-600" />
                        <span className="font-medium text-gray-800">List of Invoices: Verify button next to each</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {cashCredit.invoiceList.map((invoice, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm text-gray-700"
                          >
                            {invoice}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Settlement Action */}
            {activeRouteTab && productSettlements.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Ready to Settle</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Complete the settlement process for {routes.find(r => (r as any).routeId === activeRouteTab)?.name || activeRouteTab} {activeVehicleName ? `• ${activeVehicleName}` : ''}
                      </p>
                      
                      {/* Settlement Summary */}
                      <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="font-semibold text-blue-600">
                            {productSettlements.reduce((sum, p) => sum + p.openingStock, 0)}
                          </div>
                          <div className="text-gray-600">Items Allocated</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-red-600">
                            {productSettlements.reduce((sum, p) => sum + p.sales, 0)}
                          </div>
                          <div className="text-gray-600">Items Sold</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-green-600">
                            ₹{(cashCredit.totalCashSale + cashCredit.creditSale).toLocaleString()}
                          </div>
                          <div className="text-gray-600">Total Sales Value</div>
                        </div>
                        <div className="text-center">
                          <div className={`font-semibold ${
                            productSettlements.some(p => p.variance !== 0) || (cashCredit.totalCashSale - cashCredit.amountReceived) !== 0
                              ? 'text-yellow-600' 
                              : 'text-green-600'
                          }`}>
                            {productSettlements.some(p => p.variance !== 0) || (cashCredit.totalCashSale - cashCredit.amountReceived) !== 0
                              ? 'Discrepancy' 
                              : 'Perfect'}
                          </div>
                          <div className="text-gray-600">Status</div>
                        </div>
                      </div>

                      {/* Outlet Visits block */}
                      <div className="mt-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
                        <div className="text-sm font-medium text-gray-800 mb-2">Outlet Visits</div>
                        <div className="grid grid-cols-5 gap-4 text-sm">
                          <div className="text-center">
                            <div className="font-semibold text-gray-800">{visitStats?.totalOutletsInRoute ?? '—'}</div>
                            <div className="text-gray-600">Outlets in Route</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-blue-600">{visitStats?.visitedUniqueOutlets ?? '—'}</div>
                            <div className="text-gray-600">Visited</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-gray-700">{visitStats?.notVisited ?? '—'}</div>
                            <div className="text-gray-600">Not Visited</div>
                          </div>
                          <div className="col-span-2 flex flex-wrap items-center gap-2 justify-center">
                            {visitStats && (
                              <>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">sale {visitStats.visitedByStatus.sale}</span>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">sale+rep {visitStats.visitedByStatus.sale_and_replace}</span>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">replace {visitStats.visitedByStatus.replace_only}</span>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">visit {visitStats.visitedByStatus.visit_only}</span>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-rose-50 text-rose-700 border border-rose-200">no-sale {visitStats.visitedByStatus.no_sale}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {(() => {
                      const routeStatus = routeStatuses[activeRouteTab];
                      const isEditable = canSettle(routeStatus, selectedDate);
                      const status = routeStatus?.status;
                      const today = new Date().toISOString().split('T')[0];
                      const isCurrentDay = selectedDate === today;
                      
                      return (
                        <button
                          onClick={handleSettle}
                          disabled={settling || !isEditable}
                          className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isEditable 
                              ? 'bg-green-600 text-white hover:bg-green-700' 
                              : 'bg-gray-300 text-gray-500'
                          }`}
                          title={
                            status === 'settled' ? 'Route already settled' :
                            status === 'on_field' && isCurrentDay ? 'Waiting for salesman to mark ready for settlement' :
                            status === 'on_field' && !isCurrentDay ? 'Click to force settlement for previous day' :
                            status === 'settling' ? 'Click to complete settlement' :
                            'Settlement not available'
                          }
                        >
                          <CheckCircle className={`w-5 h-5 ${settling ? 'animate-spin' : ''}`} />
                          <span>
                            {settling ? 'Settling...' : 
                             status === 'settled' ? 'Already Settled' :
                             status === 'on_field' && isCurrentDay ? 'Waiting for Ready Status' :
                             status === 'on_field' && !isCurrentDay ? 'Force Settle' :
                             status === 'settling' ? 'Settle' :
                             'Not Available'}
                          </span>
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementPage;
